<script setup lang="ts">
import { ref, watch } from 'vue';
import { CampaignService } from '@/services/campaign.services';
import CampaignTargetProducts from './CampaignTargetProducts.vue';
import CampaignTargetCategories from './CampaignTargetCategories.vue';
import CampaignTargetPromotions from './CampaignTargetPromotions.vue';
import CollapsibleSection from '@/components/product/edit/CollapsibleSection.vue';
import { useCampaignToast } from './useCampaignToast';
import type { ICampaignTargets } from './campaign.types';

const props = defineProps<{
  storeId: string;
  campaignId: string;
  initialTargets: ICampaignTargets;
}>();

const { notifySuccess, notifyError } = useCampaignToast();

const emit = defineEmits<{
  saved: [];
}>();

const productIds = ref<string[]>([...props.initialTargets.productIds]);
const categoryIds = ref<string[]>([...props.initialTargets.categoryIds]);
const promotionIds = ref<string[]>([...props.initialTargets.promotionIds]);

watch(
  () => props.initialTargets,
  (targets) => {
    productIds.value = [...targets.productIds];
    categoryIds.value = [...targets.categoryIds];
    promotionIds.value = [...targets.promotionIds];
  },
);

const saving = ref(false);
const saveError = ref('');
const saveSuccess = ref(false);

async function save() {
  saving.value = true;
  saveError.value = '';
  saveSuccess.value = false;
  try {
    await CampaignService.setTargets(props.storeId, props.campaignId, {
      productIds: productIds.value,
      categoryIds: categoryIds.value,
      promotionIds: promotionIds.value,
    });
    saveSuccess.value = true;
    notifySuccess('Cibles de la campagne enregistrées.');
    emit('saved');
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    saveError.value = Array.isArray(raw)
      ? raw.join(' · ')
      : (raw ?? 'Erreur lors de l’enregistrement des cibles.');
    notifyError(err, 'Erreur lors de l’enregistrement des cibles.');
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="ctgp">
    <div class="ctgp__section">
      <CollapsibleSection
        section-key="campaign-target-products"
        icon="pi-box"
        title="Produits"
        default-open
      >
        <CampaignTargetProducts v-model="productIds" :store-id="storeId" />
      </CollapsibleSection>
    </div>

    <div class="ctgp__section">
      <CollapsibleSection
        section-key="campaign-target-categories"
        icon="pi-folder"
        title="Catégories"
        default-open
      >
        <CampaignTargetCategories v-model="categoryIds" :store-id="storeId" />
      </CollapsibleSection>
    </div>

    <div class="ctgp__section">
      <CollapsibleSection
        section-key="campaign-target-promotions"
        icon="pi-percentage"
        title="Promotions"
        default-open
      >
        <CampaignTargetPromotions v-model="promotionIds" :store-id="storeId" />
      </CollapsibleSection>
    </div>

    <p v-if="saveError" class="ctgp__error">{{ saveError }}</p>
    <p v-else-if="saveSuccess" class="ctgp__success">Cibles enregistrées.</p>

    <div class="ctgp__actions">
      <button
        type="button"
        class="ctgp__btn ctgp__btn--primary"
        :disabled="saving"
        @click="save"
      >
        <i v-if="saving" class="pi pi-spin pi-spinner" />
        Enregistrer les cibles
      </button>
    </div>
  </div>
</template>

<style scoped>
.ctgp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.ctgp__section {
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  background: var(--nxt-card-bg);
}
.ctgp__error {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.1);
  color: var(--nxt-error);
  font-size: 0.8125rem;
}
.ctgp__success {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-size: 0.8125rem;
}
.ctgp__actions {
  display: flex;
  justify-content: flex-end;
}
.ctgp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.ctgp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.ctgp__btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
